import { APP_TYPE, NUMBER_OF_REFRESH } from '../constants';
import { sleep } from '../utils';
import { sentryCaptureException } from '@/exceptions/sentry';

export type ShopInfoV7Response = {
  code: number;
  message: string;
  shopID: string;
  details: string | null;
  shopifyDomain?: string;
  userHash?: string;
  token: string;
  status: 'CONNECTED' | 'NOT_CONNECTED';
  firstName?: string;
  isGemPagesV7?: string;
  plan?: string;
  shopifyPlan?: string;
};

export interface IGempagesService {
  fetchShopInfoV7(gemSession: string): Promise<ShopInfoV7Response | undefined>;
}

export class GemPagesService implements IGempagesService {
  graphqlApiUrl: string;
  constructor(apiUrl: string) {
    this.graphqlApiUrl = apiUrl;
  }

  async fetchShopInfoV7(
    gemSession: string,
    retries: number = NUMBER_OF_REFRESH,
  ): Promise<ShopInfoV7Response | undefined> {
    try {
      const res = await fetch(`${this.graphqlApiUrl}shop-login/${APP_TYPE}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Gem-Session': gemSession,
          'Content-Type': 'application/json',
        },
      });

      // Handle server errors
      if (res.status >= 500) {
        if (retries > 0) {
          await sleep(500 * (NUMBER_OF_REFRESH - retries + 1));
          return this.fetchShopInfoV7(gemSession, retries - 1);
        }
        throw new Error(res.statusText);
      }

      const resJson = (await res.json()) as ShopInfoV7Response;

      // Handle GemPages API errors
      if (resJson.code === 500100) {
        if (retries > 0) {
          await sleep(500 * (NUMBER_OF_REFRESH - retries + 1));
          return this.fetchShopInfoV7(gemSession, retries - 1);
        }
      }

      // Handle permission errors
      if (res.status === 401 || [401101, 400120, 400126].includes(resJson.code)) {
        sentryCaptureException('fetchShopInfoV7', resJson.message, resJson);
      }

      return resJson;
    } catch (error) {
      sentryCaptureException('GemPagesService - fetchShopInfoV7', 'Error', error as Error, {
        level: 'error',
      });
      throw new Error((error as Error).message);
    }
  }
}
